/**
 * One person's remember state, for the contact-detail surfaces (PPL-350).
 *
 * Port of the app's hook, carrying the fixes its review produced; if a rule below
 * surprises you, the app file has the same rule with the same reasoning.
 *
 * ## Why the person doc is subscribed rather than read
 *
 * `remembered` and `pausedAt` are written by a CALLABLE, not the client, so a
 * one-shot read taken on mount is stale the instant someone uses the control —
 * the marker, the pill and the panel would all keep showing the old state until
 * the page was left and re-entered. One document, and it is what makes the click
 * feel like it did something.
 *
 * ## Failures hide the surface, they do not fake it
 *
 * A quota that fails to load leaves `quota` null and every component built on it
 * renders nothing rather than guessing at a limit. Telling someone they have
 * "4 slots left" and then refusing them is worse than showing no counter.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import { capture } from '../services/posthog';
import { MonetizationEvents, PaywallGate, PaywallSource } from '../services/monetizationEvents';
import {
  asRememberLimitError,
  fetchAccountArchive,
  fetchPersonArchive,
  fetchRememberQuota,
  rememberStateOf,
  setPersonRemembered,
  type AccountArchive,
  type PersonArchive,
  type RememberQuota,
  type RememberState,
} from '../services/rememberService';

/** Why the sheet opened — the three are different refusals and read differently. */
export type LimitMode =
  /** A sixth person: breadth. Every slot is spoken for. */
  | 'add'
  /** Un-pausing with no room: breadth again, but their story is already there. */
  | 'resume'
  /**
   * An ACTIVE person's older memories: permanence, not breadth. Nothing to do
   * with slots — telling someone with two free that "every slot is full" is
   * simply untrue, and it is the wrong thing to sell them on besides.
   */
  | 'window';

export interface LimitPrompt {
  open: boolean;
  mode: LimitMode;
}

interface Options {
  /**
   * The contact row the caller already holds.
   *
   * The subscription is authoritative but takes a round trip; without a seed the
   * control renders "f8 doesn't remember them yet" — with a button offering to
   * start — for a beat, on someone f8 has remembered for months.
   */
  seed?: unknown;
  /**
   * Surfaces a failure that is NOT the at-limit refusal. Without it these have
   * nowhere to go: the click does nothing and nothing explains why.
   */
  onError?: (message: string) => void;
}

export function useRememberPerson(
  uid: string | null | undefined,
  personId: string | null | undefined,
  options: Options = {},
) {
  const { seed, onError } = options;
  /**
   * `null` until the subscription answers. Until then the seed speaks.
   *
   * A lazy `useState` initializer would NOT do here the way it does in the app:
   * the app is handed the row it navigated from, but this page loads its contact
   * in an effect, so at mount `seed` is still null and every visit to a
   * remembered person would render "f8 doesn't remember them yet" — with a button
   * offering to start — until Firestore came back.
   */
  const [snapshotState, setSnapshotState] = useState<RememberState | null>(null);
  const state: RememberState = snapshotState ?? rememberStateOf(seed);
  const [quota, setQuota] = useState<RememberQuota | null>(null);
  const [archive, setArchive] = useState<PersonArchive | null>(null);
  const [accountArchive, setAccountArchive] = useState<AccountArchive | null>(null);
  const [busy, setBusy] = useState(false);
  const [limitPrompt, setLimitPrompt] = useState<LimitPrompt>({ open: false, mode: 'add' });
  const [errorSeen, setErrorSeen] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    // Cleared FIRST, because this route is reachable in place — an f8 card action
    // swaps `contactId` without unmounting the page. Without this the previous
    // person's numbers stay on screen until the new reads resolve: "83 memories
    // archived for <the new name>", a status pill claiming f8 remembers someone
    // it does not, and the old kept/archived split on the timeline rule.
    setSnapshotState(null);
    setArchive(null);
    if (!uid || !personId) return undefined;
    return onSnapshot(
      doc(FIRESTORE_DB, `users/${uid}/people/${personId}`),
      (snap) => setSnapshotState(rememberStateOf(snap.exists() ? snap.data() : null)),
      () => setSnapshotState('never'),
    );
  }, [uid, personId]);

  const refreshQuota = useCallback(async () => {
    try {
      const q = await fetchRememberQuota();
      if (alive.current) setQuota(q);
    } catch {
      // Keep what we had. Clearing a counter that was correct a second ago
      // because one poll failed is a worse answer than a slightly stale one.
    }
  }, []);

  const refreshArchive = useCallback(async () => {
    if (!personId) return;
    try {
      const a = await fetchPersonArchive(personId);
      if (alive.current) setArchive(a);
    } catch {
      if (alive.current) setArchive(null);
    }
  }, [personId]);

  useEffect(() => { void refreshQuota(); }, [refreshQuota]);

  // Re-counted on every state change: pausing someone moves their whole record
  // to the archive side of the line, so the row's number is wrong the moment the
  // control is used if this only ran on mount.
  useEffect(() => { void refreshArchive(); }, [refreshArchive, state]);

  /**
   * The at-limit sheet opening IS a paywall view, and the one that matters most.
   *
   * Fired here rather than in the component so it lands exactly once per open and
   * cannot be forgotten by a second caller rendering the same sheet.
   *
   * `gate_hit` is NOT fired here, and that is only safe because the control no
   * longer short-circuits: it always calls `setPersonRememberedEurW` and lets the
   * SERVER refuse, so every breadth refusal produces exactly one `gate_hit` from
   * one place. The earlier version skipped the callable whenever the client
   * already knew it was at the limit — which is the commonest path — so the
   * number the tier decision rests on only ever counted the race case.
   */
  useEffect(() => {
    if (!limitPrompt.open) return;
    capture(MonetizationEvents.PAYWALL_VIEWED, {
      source: PaywallSource.REMEMBERED_LIMIT_SHEET,
      gate: PaywallGate.REMEMBERED_PEOPLE,
      mode: limitPrompt.mode,
      limit: quota?.limit ?? null,
      used: quota?.activeCount ?? null,
    });
    // Only the open transition — `quota` moves underneath this while the sheet
    // is up and must not re-fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitPrompt.open, limitPrompt.mode]);

  /**
   * The account-wide total, loaded only when the sheet is actually opening.
   *
   * It is a fan-out across every remembered person, so it is not something to pay
   * for on a profile view — but "everything f8 has ever learned, restored" is the
   * sheet's headline benefit, and without the number it falls back to a sentence
   * that sells nothing.
   */
  useEffect(() => {
    if (!limitPrompt.open || accountArchive) return undefined;
    let cancelled = false;
    fetchAccountArchive()
      .then((a) => { if (!cancelled && alive.current) setAccountArchive(a); })
      .catch(() => { /* the benefit line falls back to its generic form */ });
    return () => { cancelled = true; };
  }, [accountArchive, limitPrompt.open]);

  const apply = useCallback(
    async (remembered: boolean) => {
      if (!personId || busy) return;
      setBusy(true);
      try {
        const q = await setPersonRemembered(personId, remembered);
        if (alive.current) {
          // The banner is page-level and nothing else clears it, so a success
          // after a failure must take it down or it sits there for the visit.
          if (errorSeen) { setErrorSeen(false); if (onError) onError(''); }
          // The mutation serves neither the window nor the free-tier pair
          // (PPL-383), so keep what we have rather than dropping those rows
          // until the next quota poll.
          setQuota((prev) => ({
            ...q,
            memoryWindowDays: prev?.memoryWindowDays ?? null,
            freeLimit: prev?.freeLimit ?? null,
            freeMemoryWindowDays: prev?.freeMemoryWindowDays ?? null,
          }));
        }
        void refreshArchive();
      } catch (error) {
        const limit = asRememberLimitError(error);
        if (limit && alive.current) {
          // ONLY this error sells. Anything else is a failure, not a paywall.
          setLimitPrompt({ open: true, mode: state === 'paused' ? 'resume' : 'add' });
          setQuota((prev) => (prev
            ? { ...prev, limit: limit.limit, activeCount: limit.activeCount, remaining: 0 }
            : prev));
        } else if (alive.current) {
          // Deliberately NOT `error.message`. A callable failure surfaces as the
          // literal string "internal", which tells the user nothing and reads as
          // a leak. The real message goes to the console for whoever is debugging.
          console.warn('[remember] setPersonRemembered failed:', error);
          setErrorSeen(true);
          if (onError) onError("that didn't go through — try again in a moment.");
        }
      } finally {
        if (alive.current) setBusy(false);
      }
    },
    [busy, errorSeen, onError, personId, refreshArchive, state],
  );

  return {
    state,
    quota,
    archive,
    accountArchive,
    busy,
    limitPrompt,
    /** Turn on, or resume. */
    remember: () => { void apply(true); },
    /** Pause — never deletes. */
    pause: () => { void apply(false); },
    /**
     * Open the sheet without attempting a refused write.
     *
     * The mode is explicit for callers that know WHY they are refusing — the
     * archive row's "restore" on an actively-remembered person is a window
     * refusal, not a slot one. Omitted, it infers from the person's state.
     */
    showLimit: (mode?: LimitMode) =>
      setLimitPrompt({ open: true, mode: mode ?? (state === 'paused' ? 'resume' : 'add') }),
    dismissLimit: () => setLimitPrompt((p) => ({ ...p, open: false })),
    refreshQuota,
  };
}

export default useRememberPerson;
